import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControlLabel,
  Switch,
  CircularProgress,
} from '@mui/material';
import emailSettingsApi from '@crema/services/apis/emailSettings';

const AddRecipientDialog = ({
  open,
  onClose,
  onSuccess,
  onError,
  recipient,
  isEdit = false,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    is_active: true,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (isEdit && recipient) {
        setFormData({
          name: recipient.name || '',
          email: recipient.email || '',
          is_active:
            recipient.is_active !== undefined ? recipient.is_active : true,
        });
      } else {
        setFormData({
          name: '',
          email: '',
          is_active: true,
        });
      }
      setErrors({});
    }
  }, [open, recipient, isEdit]);

  const handleInputChange = (field) => (event) => {
    const value =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Valid email address is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      onError('Please fix form errors before saving');
      return;
    }

    try {
      setSaving(true);

      let response;
      if (isEdit) {
        response = await emailSettingsApi.updateRecipient(
          recipient.id,
          formData,
        );
      } else {
        response = await emailSettingsApi.addRecipient(formData);
      }

      if (response.data.status === 200) {
        onSuccess(`Recipient ${isEdit ? 'updated' : 'added'} successfully`);
      } else {
        onError(
          response.data.message ||
            `Failed to ${isEdit ? 'update' : 'add'} recipient`,
        );
      }
    } catch (error) {
      console.error(
        `Error ${isEdit ? 'updating' : 'adding'} recipient:`,
        error,
      );
      onError(`Failed to ${isEdit ? 'update' : 'add'} recipient`);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        {isEdit ? 'Edit Recipient' : 'Add New Recipient'}
      </DialogTitle>

      <DialogContent>
        <Box sx={{pt: 1}}>
          <TextField
            fullWidth
            label='Name'
            value={formData.name}
            onChange={handleInputChange('name')}
            error={!!errors.name}
            helperText={errors.name || 'Recipient display name'}
            margin='normal'
            required
          />

          <TextField
            fullWidth
            label='Email Address'
            type='email'
            value={formData.email}
            onChange={handleInputChange('email')}
            error={!!errors.email}
            helperText={errors.email || 'Recipient email address'}
            margin='normal'
            required
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={handleInputChange('is_active')}
              />
            }
            label='Active (will receive daily reports)'
            sx={{mt: 2}}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant='contained' disabled={saving}>
          {saving ? (
            <>
              <CircularProgress size={16} sx={{mr: 1}} />
              {isEdit ? 'Updating...' : 'Adding...'}
            </>
          ) : isEdit ? (
            'Update'
          ) : (
            'Add'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddRecipientDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  recipient: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default AddRecipientDialog;
